<template>
  <div class="llm-simulation-demo">
    <audio ref="demoWavEl" class="llm-simulation-demo__demo-audio" :src="demoWav" preload="auto" />
    <div class="llm-simulation-demo__toolbar">
      <span class="llm-simulation-demo__title">模拟通话</span>
    </div>

    <div class="llm-simulation-demo__body">
      <div ref="simViewport" class="llm-simulation-demo__viewport">
        <div class="llm-simulation-demo__fit-outer" :style="fitOuterStyle">
          <div ref="simInner" class="llm-simulation-demo__fit-inner" :style="fitInnerStyle">
          <!-- 待机：大图 + 模拟演示（演示音为 video/demo.wav） -->
          <div v-show="!callActive" class="llm-vcall llm-vcall--idle">
            <div class="llm-vcall__stage">
              <img class="llm-vcall__avatar" :src="avatarSrc" alt="" @load="measureFit" />
            </div>
            <Button type="primary" class="llm-vcall__start" @click="startCall">
              <Icon type="ios-mic" size="18" />
              <span>模拟演示</span>
            </Button>
          </div>

          <!-- 通话中：图2 波形 + 挂断 + 计时 -->
          <div v-show="callActive" class="llm-vcall llm-vcall--active">
            <div class="llm-vcall__stage">
              <video
                ref="callVideoEl"
                class="llm-vcall__avatar llm-vcall__avatar--video"
                :src="callVideoSrc"
                playsinline
                loop
                muted
                @loadedmetadata="measureFit"
              />
            </div>
            <div class="llm-vcall__wave" aria-hidden="true">
              <span
                v-for="n in 18"
                :key="'w-' + n"
                class="llm-vcall__wave-bar"
                :style="{ animationDelay: (n * 0.06) + 's' }"
              />
            </div>
            <button type="button" class="llm-vcall__hangup" title="挂断" @click="endCall">
              <Icon type="ios-close" size="28" />
            </button>
            <div class="llm-vcall__timer">{{ formattedElapsed }}</div>
          </div>
        </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import manImg from '../../video/man.png'
import womanImg from '../../video/woman.png'
import callMaleMp4 from '../../video/man.mp4'
import callFemaleMp4 from '../../video/woman.mp4'
import demoWav from '../../video/demo_woman.wav'

import { PRESET_MODE } from '../../utils/tripleAccessHelpers'
import { PRESET_VOICES_ALIYUN, PRESET_VOICES_QWEN_OMNI, getStaleVoiceAuditionMessage } from '../../constants/voicePresets'

const MALE_PRESET_IDS = new Set(
  [...PRESET_VOICES_ALIYUN, ...PRESET_VOICES_QWEN_OMNI]
    .filter((p) => p.emoji === '👨')
    .map((p) => p.id),
)

export default {
  name: 'LlmSimulationDemo',
  props: {
    voiceCall: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      demoWav,
      callActive: false,
      callStartedAt: null,
      tickTimer: null,
      nowTick: 0,
      fitScale: 1,
      innerMeasure: { w: 320, h: 420 },
      fitViewportResizeObs: null,
      fitWindowResizeHandler: null,
    }
  },
  computed: {
    /** 当前音色是否为男性（系统预设 id / 克隆名称启发） */
    avatarIsMale() {
      const vc = this.voiceCall
      if (!vc) return false
      const isPresetEngine = (vc.presetMode || PRESET_MODE.ENGINE) === PRESET_MODE.ENGINE
      if (isPresetEngine) {
        return MALE_PRESET_IDS.has(String(vc.presetVoiceId || '').trim())
      }
      const row =
        (vc.clonedVoices || []).find((x) => x && x.selection) || (vc.clonedVoices || [])[0]
      if (!row) return false
      const gender = row.gender != null ? String(row.gender).trim().toLowerCase() : ''
      if (gender === 'male' || gender === '男') return true
      if (gender === 'female' || gender === '女') return false
      return this.inferMaleFromCloneName(row.name)
    },
    avatarSrc() {
      return this.avatarIsMale ? manImg : womanImg
    },
    /** 模拟演示通话中时按当前所选音色性别播放 video 下对应 mp4（静音画面） */
    callVideoSrc() {
      return this.avatarIsMale ? callMaleMp4 : callFemaleMp4
    },
    elapsedSeconds() {
      if (!this.callActive || !this.callStartedAt) return 0
      return Math.max(0, Math.floor((this.nowTick - this.callStartedAt) / 1000))
    },
    formattedElapsed() {
      const s = this.elapsedSeconds
      const mm = String(Math.floor(s / 60)).padStart(2, '0')
      const ss = String(s % 60).padStart(2, '0')
      return `${mm}:${ss}`
    },
    fitOuterStyle() {
      const s = this.fitScale
      const { w, h } = this.innerMeasure
      return {
        width: `${Math.ceil(w * s)}px`,
        height: `${Math.ceil(h * s)}px`,
        overflow: 'hidden',
        flexShrink: 0,
      }
    },
    fitInnerStyle() {
      const s = this.fitScale
      const { w } = this.innerMeasure
      return {
        width: `${w}px`,
        transform: `scale(${s})`,
        transformOrigin: 'top left',
      }
    },
  },
  watch: {
    callActive() {
      this.$nextTick(() => this.measureFit())
    },
    voiceCall: {
      deep: true,
      handler() {
        this.$nextTick(() => this.measureFit())
      },
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.bindFitObservers()
      this.measureFit()
    })
  },
  beforeDestroy() {
    this.unbindFitObservers()
    this.clearTickTimer()
    this.pauseCallVideo()
    this.pauseDemoWav()
  },
  methods: {
    measureFit() {
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          const vp = this.$refs.simViewport
          const inner = this.$refs.simInner
          if (!vp || !inner) return
          const availW = Math.max(1, vp.clientWidth)
          const availH = Math.max(1, vp.clientHeight)
          const cw = Math.max(1, inner.scrollWidth)
          const ch = Math.max(1, inner.scrollHeight)
          this.innerMeasure = { w: cw, h: ch }
          const s = Math.min(availW / cw, availH / ch, 1)
          this.fitScale = Number.isFinite(s) && s > 0 ? s : 1
        })
      })
    },
    bindFitObservers() {
      this.unbindFitObservers()
      const vp = this.$refs.simViewport
      this.fitWindowResizeHandler = () => this.measureFit()
      window.addEventListener('resize', this.fitWindowResizeHandler, { passive: true })
      if (!vp || typeof ResizeObserver === 'undefined') return
      this.fitViewportResizeObs = new ResizeObserver(() => this.measureFit())
      this.fitViewportResizeObs.observe(vp)
    },
    unbindFitObservers() {
      if (this.fitViewportResizeObs) {
        try {
          this.fitViewportResizeObs.disconnect()
        } catch (e) {
          /* noop */
        }
        this.fitViewportResizeObs = null
      }
      if (this.fitWindowResizeHandler) {
        window.removeEventListener('resize', this.fitWindowResizeHandler)
        this.fitWindowResizeHandler = null
      }
    },
    inferMaleFromCloneName(name) {
      const s = String(name || '').trim()
      if (!s) return false
      if (/男声|男士|男|帅哥|先生|male|MALE|\bMale\b/.test(s)) return true
      if (/女声|女士|女|小姐|姐妹|female|Female|\bFemale\b/.test(s)) return false
      return false
    },
    startCall() {
      const staleMsg = getStaleVoiceAuditionMessage(this.voiceCall)
      if (staleMsg) {
        this.$Message.warning(staleMsg)
        return
      }
      this.callActive = true
      this.callStartedAt = Date.now()
      this.nowTick = Date.now()
      this.clearTickTimer()
      this.tickTimer = setInterval(() => {
        this.nowTick = Date.now()
      }, 250)
      this.$nextTick(() => {
        this.playCallVideo()
        this.playDemoWav()
      })
    },
    playDemoWav() {
      const el = this.$refs.demoWavEl
      if (!el) return
      try {
        el.currentTime = 0
        const p = el.play()
        if (p && typeof p.catch === 'function') {
          p.catch(() => {})
        }
      } catch (e) {
        /* noop */
      }
    },
    pauseDemoWav() {
      const el = this.$refs.demoWavEl
      if (!el) return
      try {
        el.pause()
        el.currentTime = 0
      } catch (e) {
        /* noop */
      }
    },
    playCallVideo() {
      const el = this.$refs.callVideoEl
      if (!el) return
      try {
        el.currentTime = 0
        const p = el.play()
        if (p && typeof p.catch === 'function') {
          p.catch(() => {
            try {
              el.muted = true
              const p2 = el.play()
              if (p2 && typeof p2.catch === 'function') p2.catch(() => {})
            } catch (e) {
              /* noop */
            }
          })
        }
      } catch (e) {
        /* noop */
      }
    },
    pauseCallVideo() {
      const el = this.$refs.callVideoEl
      if (!el) return
      try {
        el.pause()
      } catch (e) {
        /* noop */
      }
    },
    endCall() {
      this.pauseCallVideo()
      this.pauseDemoWav()
      this.callActive = false
      this.callStartedAt = null
      this.clearTickTimer()
    },
    clearTickTimer() {
      if (this.tickTimer) {
        clearInterval(this.tickTimer)
        this.tickTimer = null
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.llm-simulation-demo {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  align-self: stretch;
  min-height: 0;
  height: 100%;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  border: 1px solid #e8e8e8;
}

.llm-simulation-demo__demo-audio {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
}

.llm-simulation-demo__toolbar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 10px 12px;
  border-bottom: 1px solid #f0f0f0;
  background: #fafafa;
}

.llm-simulation-demo__title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.llm-simulation-demo__body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 0 12px 16px;
  background: linear-gradient(180deg, #fafbfc 0%, #fff 40%);
}

/* 占满工具栏以下剩余高度，供 measureFit 计算可用区域并整体 scale */
.llm-simulation-demo__viewport {
  flex: 1 1 auto;
  /* 小屏 / 双栏纵向堆叠：与原先紧凑方案一致 */
  margin-top: 1rem;
  min-height: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  overflow: hidden;
}

/*
 * 大屏双栏（与 llm/index.vue @media max-width:1100px 对应：侧栏在右侧时）
 * 预览区与左侧内容视觉对齐，预留顶部 14rem；高度不足时仍由 measureFit 整体缩小。
 */
@media (min-width: 1101px) {
  .llm-simulation-demo__viewport {
    margin-top: 14rem;
  }
}

.llm-simulation-demo__fit-outer {
  margin: 0 auto;
  flex-shrink: 0;
}

.llm-simulation-demo__fit-inner {
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

.llm-vcall {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 320px;
  margin: 0 auto;
  box-sizing: border-box;
}

.llm-vcall__stage {
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
  background: #f5f5f5;
  margin-bottom: 16px;
}

.llm-vcall__avatar {
  display: block;
  width: 100%;
  height: auto;
  // vertical-align: middle;
  object-fit: cover;
  aspect-ratio: 3 / 4;
}

.llm-vcall__avatar--video {
  background: #000;
}

.llm-vcall__start {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 22px;
  font-size: 15px;
  border-radius: 8px;
}

.llm-vcall--active .llm-vcall__stage {
  margin-bottom: 12px;
}

.llm-vcall__wave {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  height: 36px;
  margin-bottom: 14px;
  padding: 0 8px;
}

.llm-vcall__wave-bar {
  width: 4px;
  height: 28px;
  transform-origin: center bottom;
  border-radius: 2px;
  background: linear-gradient(180deg, #69c0ff 0%, #1890ff 50%, #096dd9 100%);
  animation: llm-vcall-wave 1.1s ease-in-out infinite alternate;
}

@keyframes llm-vcall-wave {
  from {
    transform: scaleY(0.25);
    opacity: 0.75;
  }
  to {
    transform: scaleY(1);
    opacity: 1;
  }
}

.llm-vcall__hangup {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: none;
  margin-bottom: 10px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: #ff4d4f;
  box-shadow: 0 4px 12px rgba(255, 77, 79, 0.45);
  transition:
    transform 0.15s ease,
    box-shadow 0.15s ease;

  &:hover {
    background: #ff7875;
    transform: scale(1.04);
  }

  &:active {
    transform: scale(0.96);
  }
}

.llm-vcall__timer {
  font-size: 18px;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  color: #262626;
  letter-spacing: 0.04em;
}

@media (prefers-reduced-motion: reduce) {
  .llm-vcall__wave-bar {
    animation: none;
    transform: scaleY(0.55);
    opacity: 0.9;
  }
}
</style>
